// Frontend submitter for both the project-enquiry and career forms.
//
// This posts straight to our own PHP endpoint (public/api/contact.php,
// copied as-is into build/api/contact.php by CRA), which sends the message
// via SMTP into the TowerEdge inbox — with a proper Subject, Reply-To set
// to the visitor's email, and any attached file carried as a real email
// attachment. No third-party relay (FormSubmit) is involved anymore, so
// replying from the inbox goes straight back to the visitor.

const ENDPOINT = '/api/contact.php';

/**
 * @param {Record<string, string>} fields - form field name/value pairs. A
 *   truthy `website` field is treated as the honeypot and silently no-ops.
 * @param {{ formType: 'project' | 'career', file?: File|null }} options
 */
export async function submitContactForm(fields, { formType, file } = {}) {
  // Honeypot — bots fill hidden "website"; real users leave it empty
  if (fields.website && String(fields.website).trim()) {
    return { success: true, skipped: true };
  }

  const body = new FormData();
  body.append('formType', formType || 'project');
  Object.entries(fields).forEach(([key, value]) => {
    body.append(key, value ?? '');
  });
  if (file) {
    body.append('attachment', file, file.name);
  }

  const response = await fetch(ENDPOINT, { method: 'POST', body });
  const data = await response.json().catch(() => ({}));

  if (!response.ok || data.success === false) {
    const error = new Error(
      data.message || 'Failed to send your submission. Please try again.',
    );
    error.fieldErrors = data.errors || null;
    throw error;
  }

  return data;
}

/** Project/general enquiry form. */
export async function sendContactEmail({
  name,
  company,
  phone,
  email,
  message,
  website = '',
  file = null,
}) {
  return submitContactForm(
    { name, company: company || '', phone, email, message, website },
    { formType: 'project', file },
  );
}

/** Careers application form. */
export async function sendCareerApplication({
  name,
  phone,
  email,
  message,
  website = '',
  resume,
}) {
  return submitContactForm(
    { name, phone, email, message, website },
    { formType: 'career', file: resume },
  );
}
